// Ported from renderView()/updateMainTabs()/refreshSidebar() (source lines
// 3239-3326, 8525-8567). Dispatches STATE.view to the matching screen module.
// Also owns URL <-> STATE routing (navigate()/syncUrl()/popstate) so browser
// Back/Forward and page refresh both work - see docs on the routing addition.
import { $, $$, calcAge, escapeHtml, closeModal } from "./dom.js";
import { STATE, canViewAudit, canImportData, type ViewName } from "./state.js";
import { patientsApi, sitesApi, studiesApi } from "./api-client.js";
import { renderStudiesView } from "./studies-view.js";
import { renderECRF } from "./visits-view.js";
import { renderDeviationsView } from "./safety-view.js";
import { renderQueriesView } from "./queries-view.js";
import { renderAuditView } from "./audit-view.js";
import { renderAdminView } from "./admin-view.js";

function showSidebarFor(s: typeof STATE): boolean {
  return s.view === "ecrf" || (s.view === "studies" && s.currentStudyId != null);
}

export async function renderView(): Promise<void> {
  updateMainTabs();
  const root = $("#contentArea")!;
  $("#sidebar")!.classList.toggle("hidden", !showSidebarFor(STATE));

  switch (STATE.view) {
    case "studies":
      await renderStudiesView(root);
      break;
    case "ecrf":
      await renderECRF(root);
      break;
    case "dev":
      await renderDeviationsView(root);
      break;
    case "q":
      await renderQueriesView(root);
      break;
    case "audit":
      if (canViewAudit()) await renderAuditView(root);
      else {
        STATE.view = "studies";
        syncUrl(true);
        await renderStudiesView(root);
      }
      break;
    case "admin":
      if (canImportData()) await renderAdminView(root);
      else {
        STATE.view = "studies";
        syncUrl(true);
        await renderStudiesView(root);
      }
      break;
  }
}

function updateMainTabs(): void {
  $$(".maintab").forEach((btn) => {
    const view = btn.getAttribute("data-view");
    btn.classList.toggle("active", view === STATE.view);
  });
  $(".admin-only")!.classList.toggle("hidden", !canImportData());
  $(".audit-only")!.classList.toggle("hidden", !canViewAudit());
}

// ---- URL <-> STATE ----

const FLAT_VIEW_PATHS: Record<string, ViewName> = { deviations: "dev", queries: "q", audit: "audit", admin: "admin" };
const VIEW_PATHS: Record<string, string> = { dev: "/deviations", q: "/queries", audit: "/audit", admin: "/admin" };

function computeUrl(s: typeof STATE): string {
  let path: string;
  if (s.view === "ecrf") {
    if (s.currentPatientId == null) path = "/patients";
    else if (s.currentStudyId != null) path = `/studies/${s.currentStudyId}/patients/${encodeURIComponent(s.currentPatientId)}`;
    else path = `/patients/${encodeURIComponent(s.currentPatientId)}`;
  } else if (s.view === "studies") {
    path = s.currentStudyId != null ? `/studies/${s.currentStudyId}` : "/studies";
  } else {
    path = VIEW_PATHS[s.view] ?? "/studies";
  }

  const params = new URLSearchParams();
  if (s.view === "ecrf" && s.currentPatientId != null) {
    // Absence means the default (V0 / overview, see visits-view.ts) - only
    // non-default values get serialized.
    if (s.currentVisitKey && s.currentVisitKey !== "V0") params.set("visit", s.currentVisitKey);
    if (s.currentSection && s.currentSection !== "overview") params.set("section", s.currentSection);
  }
  const qs = params.toString();
  return qs ? `${path}?${qs}` : path;
}

type NavPatch = Partial<Pick<typeof STATE, "view" | "currentStudyId" | "currentPatientId" | "currentVisitKey" | "currentSection">>;

export function parseUrl(target: string): NavPatch {
  const [pathname, search] = target.split("?");
  const params = new URLSearchParams(search ?? "");
  const segs = (pathname ?? "/").split("/").filter(Boolean);

  if (segs.length === 1 && FLAT_VIEW_PATHS[segs[0]!]) {
    return { view: FLAT_VIEW_PATHS[segs[0]!] };
  }

  if (segs[0] === "patients") {
    return {
      view: "ecrf",
      currentStudyId: null,
      currentPatientId: segs[1] ? decodeURIComponent(segs[1]) : null,
      currentVisitKey: params.get("visit"),
      currentSection: params.get("section"),
    };
  }

  if (segs[0] === "studies") {
    const studyId = segs[1] ? Number(segs[1]) : null;
    const validStudyId = studyId != null && Number.isFinite(studyId) ? studyId : null;
    if (segs[2] === "patients" && segs[3]) {
      return {
        view: "ecrf",
        currentStudyId: validStudyId,
        currentPatientId: decodeURIComponent(segs[3]),
        currentVisitKey: params.get("visit"),
        currentSection: params.get("section"),
      };
    }
    return { view: "studies", currentStudyId: validStudyId };
  }

  return { view: "studies", currentStudyId: null };
}

// URL-only sync from the current STATE - no render, no sidebar refresh.
// Used by in-page leaf renderers (visit/section tab switches) and by
// error/permission fallbacks that already re-render themselves directly.
export function syncUrl(replace = true): void {
  const url = computeUrl(STATE);
  if (replace) history.replaceState(null, "", url);
  else history.pushState(null, "", url);
}

// Page-level navigation: merge into STATE, update history, refresh the
// sidebar if the resulting view needs it, re-render. Use for anything that
// should be a Back-button stop.
export function navigate(patch: NavPatch, opts?: { replace?: boolean }): void {
  Object.assign(STATE, patch);
  syncUrl(opts?.replace ?? false);
  // Header title/badge reflect the current study everywhere, not just on
  // pages that show the sidebar, so this updates independently of the
  // refreshSidebar() call below (which also touches it, redundantly but
  // harmlessly, once the patient list itself needs refreshing).
  updateCurrentStudyBadge();
  if (showSidebarFor(STATE)) void refreshSidebar();
  void renderView();
}

export function setupAppNav(): void {
  $$(".maintab").forEach((btn) => {
    btn.addEventListener("click", () => {
      navigate({ view: btn.getAttribute("data-view") as ViewName });
    });
  });
  $("#btnNewPatient")!.addEventListener("click", () => {
    import("./patients-view.js").then((m) => m.openNewPatientModal());
  });
  $("#btnSwitchStudy")?.addEventListener("click", () => {
    navigate({ currentStudyId: null, view: "studies" });
  });
  $("#patientSearch")!.addEventListener("input", (e) => {
    sidebarSearchQuery = (e.target as HTMLInputElement).value;
    renderPatientListItems(filterPatients(lastSortedPatients, sidebarSearchQuery));
  });

  // Back/Forward: always re-derive STATE from the URL rather than trusting
  // history.state, so it survives bfcache restores. A stale open modal
  // shouldn't sit over a page whose STATE just changed out from under it.
  window.addEventListener("popstate", () => {
    closeModal();
    Object.assign(STATE, parseUrl(location.pathname + location.search));
    updateCurrentStudyBadge();
    if (showSidebarFor(STATE)) void refreshSidebar();
    void renderView();
  });
}

// Status class/label maps ported verbatim from source refreshSidebar()
// (source lines 3275-3288).
const STATUS_CLASS: Record<string, string> = {
  SCREENING: "screening",
  ACTIVE: "active-st",
  COMPLETED: "completed",
  "WITHDRAWAL BY SUBJECT": "withdrawn",
  "SCREEN FAILURE": "screen-fail",
};
const STATUS_LABEL: Record<string, string> = {
  SCREENING: "Screening",
  ACTIVE: "Actif",
  COMPLETED: "Terminé",
  "WITHDRAWAL BY SUBJECT": "Sortie",
  "SCREEN FAILURE": "Screen Fail",
};

// Search is a new feature (not in the source app): client-side only, since
// the full patient list is already in memory after refreshSidebar() fetches
// it - no extra API calls per keystroke. Matches against ID, breed and the
// displayed status label so "actif"/"screen fail" etc. also work as search terms.
let lastSortedPatients: any[] = [];
let sidebarSearchQuery = "";
// siteid -> site name, e.g. "01" -> "CHUV VetAgroSup" (site names aren't on
// the patient record itself, only siteid - fetched once alongside patients).
let siteNameByCode: Record<string, string> = {};
// studyId -> study name, same idea: patients only carry studyId.
let studyNameById: Record<number, string> = {};

function filterPatients(patients: any[], query: string): any[] {
  const q = query.trim().toLowerCase();
  if (!q) return patients;
  return patients.filter((p) => {
    const statusLabel = (STATUS_LABEL[p.status] || p.status || "").toLowerCase();
    const siteName = (siteNameByCode[p.siteid] || "").toLowerCase();
    const studyName = (p.studyId != null ? studyNameById[p.studyId] || "" : "").toLowerCase();
    return (
      String(p.id).toLowerCase().includes(q) ||
      String(p.breed || "").toLowerCase().includes(q) ||
      statusLabel.includes(q) ||
      siteName.includes(q) ||
      studyName.includes(q) ||
      String(p.siteid || "").toLowerCase().includes(q)
    );
  });
}

function renderPatientListItems(patients: any[]): void {
  const container = $("#patientList")!;
  if (lastSortedPatients.length === 0) {
    container.innerHTML = `<div class="empty-state" style="padding:30px 16px"><div class="icon">📁</div><p style="font-size:12px">Aucun patient.<br>Cliquez sur "Nouveau patient" pour démarrer.</p></div>`;
    return;
  }
  if (patients.length === 0) {
    container.innerHTML = `<div class="empty-state" style="padding:24px 16px"><div class="icon">🔍</div><p style="font-size:12px">Aucun patient ne correspond à « ${escapeHtml(sidebarSearchQuery)} ».</p></div>`;
    return;
  }
  container.innerHTML = patients
    .map((p: any) => {
      const siteName = siteNameByCode[p.siteid] || p.siteid;
      const studyName = p.studyId != null ? studyNameById[p.studyId] : null;
      return `
      <div class="patient-item ${p.id === STATE.currentPatientId ? "active" : ""}" data-pid="${p.id}">
        <div class="pid">${escapeHtml(p.id)}</div>
        <div class="pinfo">${escapeHtml(p.breed || "—")} · ${p.brthdtc ? calcAge(p.brthdtc) : ""} · ${escapeHtml(p.sex || "?")}</div>
        <div class="psite" title="Site investigateur">📍 ${escapeHtml(siteName)}</div>
        ${studyName ? `<div class="pstudy" title="Étude clinique">🧪 ${escapeHtml(studyName)}</div>` : ""}
        <span class="pstatus ${STATUS_CLASS[p.status] || ""}">${STATUS_LABEL[p.status] || p.status}</span>
      </div>`;
    })
    .join("");
  $$(".patient-item", container).forEach((el) => {
    el.addEventListener("click", () => {
      navigate({
        currentPatientId: el.getAttribute("data-pid"),
        currentVisitKey: null,
        currentSection: null,
        view: "ecrf",
      });
    });
  });
}

export async function refreshSidebar(): Promise<void> {
  const [sites, studies] = await Promise.all([sitesApi.list(), studiesApi.list()]);
  siteNameByCode = Object.fromEntries(sites.map((s: any) => [s.code, s.name]));
  studyNameById = Object.fromEntries(studies.map((s: any) => [s.id, s.name]));

  updateCurrentStudyBadge();

  // With no study picked, the sidebar still shows every patient the caller's
  // own site permissions allow (patientsApi.list() is already site-scoped
  // server-side) rather than nothing - only the studyId filter is dropped.
  const list = await patientsApi.list(undefined, STATE.currentStudyId ?? undefined);
  STATE.patients = list;
  $("#patientCount")!.textContent = String(list.length);
  // Grouped by site, sorted by sequence - matches source's sidebar ordering.
  lastSortedPatients = [...list].sort((a: any, b: any) => {
    if (a.siteid !== b.siteid) return a.siteid.localeCompare(b.siteid);
    return a.sequence - b.sequence;
  });
  renderPatientListItems(filterPatients(lastSortedPatients, sidebarSearchQuery));
}

function updateCurrentStudyBadge(): void {
  const appTitle = $("#hdrAppTitle");
  const studyName = STATE.currentStudyId != null ? (studyNameById[STATE.currentStudyId] ?? `Étude #${STATE.currentStudyId}`) : null;
  if (appTitle) appTitle.textContent = studyName ? `eCRF — ${studyName}` : "eCRF";

  const badge = $("#hdrStudyBadge");
  if (!badge) return;
  if (studyName == null) {
    badge.classList.add("hidden");
    return;
  }
  badge.classList.remove("hidden");
  $("#hdrStudyName")!.textContent = studyName;
}
